import React from 'react';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Target, Users, TrendingDown, AlertTriangle, BarChart3, 
  CheckCircle, Database, FileText, Activity, ArrowRight, Zap 
} from 'lucide-react';

const ObjectivePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-6">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Beneficiary Enrollment Analysis
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
              Comprehensive analysis framework to optimize beneficiary enrollment across WCD schemes
            </p>
            <Button 
              onClick={() => window.location.href = '/beneficiary-analysis'}
              className="bg-gradient-to-r from-green-500 to-blue-600 text-white px-8 py-6 text-lg"
            >
              <Zap className="w-5 h-5 mr-2" />
              View Data-Driven Solution →
            </Button>
          </div>

          {/* Objective Section */}
          <Card className="mb-8 border-l-4 border-blue-600">
            <CardHeader>
              <CardTitle className="flex items-center text-2xl">
                <Target className="w-6 h-6 mr-3 text-blue-600" />
                Objective
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4 text-lg">
                To analyze beneficiary data across multiple schemes to:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-600 rounded-lg">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-1">Multi-Scheme Enrollment</h4>
                        <p className="text-sm text-blue-800">Identify beneficiaries enrolled in multiple schemes</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-orange-600 rounded-lg">
                        <TrendingDown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-orange-900 mb-1">Drop-off Analysis</h4>
                        <p className="text-sm text-orange-800">Determine number and percentage of drop-outs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-red-50 border-red-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-red-600 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-red-900 mb-1">Systemic Gaps</h4>
                        <p className="text-sm text-red-800">Investigate black holes causing inefficiencies</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Scope Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="w-6 h-6 mr-3 text-purple-600" />
                Scope
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Data sources:</span> All beneficiary enrollment and exit records from relevant schemes
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Timeframe:</span> Analysis period (e.g., last 12 months or financial year)
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Schemes included:</span> All WCD schemes under review (Field Monitoring, THR, HCM, PURNA, etc.)
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Key Deliverables */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="w-6 h-6 mr-3 text-green-600" />
                Key Deliverables
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Cross-Enrollment Analysis */}
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <h4 className="font-bold text-blue-900 mb-3 flex items-center">
                    <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-2 text-sm">1</span>
                    Cross-Enrollment Analysis
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      Identify beneficiaries enrolled in more than one scheme
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      Summary table and visualization (Venn diagram/network graph)
                    </li>
                  </ul>
                </div>

                {/* Drop-Off Analysis */}
                <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-lg border border-orange-200">
                  <h4 className="font-bold text-orange-900 mb-3 flex items-center">
                    <span className="bg-orange-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-2 text-sm">2</span>
                    Drop-Off Analysis
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      Calculate total drop-offs per scheme
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      Trend analysis over time (monthly/quarterly)
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      Percentage of drop-offs relative to total enrollment
                    </li>
                  </ul>
                </div>

                {/* Root Cause Analysis */}
                <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                  <h4 className="font-bold text-purple-900 mb-3 flex items-center">
                    <span className="bg-purple-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-2 text-sm">3</span>
                    Root Cause Analysis
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      Categorize reasons for drop-offs
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      Highlight patterns or clusters of drop-offs
                    </li>
                  </ul>
                </div>

                {/* Black Hole Identification */}
                <div className="p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-lg border border-red-200">
                  <h4 className="font-bold text-red-900 mb-3 flex items-center">
                    <span className="bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center mr-2 text-sm">4</span>
                    Black Hole Identification
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      Detect systemic gaps in enrollment/transitions
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      Highlight data inconsistencies or missing records
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Requirements */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Database className="w-6 h-6 mr-3 text-indigo-600" />
                Data Requirements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Badge className="mb-2 bg-indigo-100 text-indigo-700">Required Field</Badge>
                  <p className="text-sm font-semibold">Beneficiary Unique ID</p>
                  <p className="text-xs text-gray-600">Aadhaar or system-generated ID</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Badge className="mb-2 bg-indigo-100 text-indigo-700">Required Field</Badge>
                  <p className="text-sm font-semibold">Scheme Enrollment Details</p>
                  <p className="text-xs text-gray-600">Scheme name, start date, end date</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Badge className="mb-2 bg-indigo-100 text-indigo-700">Required Field</Badge>
                  <p className="text-sm font-semibold">Drop-off Reason Codes</p>
                  <p className="text-xs text-gray-600">Reason codes or remarks</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Badge className="mb-2 bg-indigo-100 text-indigo-700">Required Field</Badge>
                  <p className="text-sm font-semibold">Demographic Details</p>
                  <p className="text-xs text-gray-600">Age, gender, location</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Badge className="mb-2 bg-green-100 text-green-700">Optional</Badge>
                  <p className="text-sm font-semibold">Transaction Logs</p>
                  <p className="text-xs text-gray-600">If available for detailed tracking</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Analysis Approach */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="w-6 h-6 mr-3 text-pink-600" />
                Analysis Approach
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { step: 1, title: "Data Cleaning", desc: "Remove duplicates, validate IDs", color: "blue" },
                  { step: 2, title: "Cross-Matching", desc: "Use unique IDs to map beneficiaries across schemes", color: "purple" },
                  { step: 3, title: "Drop-Off Metrics", desc: "Compute counts, percentages, and trends", color: "orange" },
                  { step: 4, title: "Root Cause Categorization", desc: "Group reasons and analyze frequency", color: "pink" },
                  { step: 5, title: "Gap Detection", desc: "Identify missing transitions or incomplete records", color: "red" }
                ].map((item) => (
                  <div key={item.step} className={`flex items-center gap-4 p-4 bg-${item.color}-50 rounded-lg border border-${item.color}-200`}>
                    <div className={`flex-shrink-0 w-10 h-10 bg-${item.color}-600 text-white rounded-full flex items-center justify-center font-bold`}>
                      {item.step}
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900">{item.title}</h5>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Output Format */}
          <Card className="mb-8 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <FileText className="w-6 h-6 mr-3" />
                Expected Output Format
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-white/10 backdrop-blur-sm rounded-lg">
                  <h4 className="font-bold mb-3 flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Interactive Dashboard
                  </h4>
                  <ul className="space-y-2 text-sm text-white/90">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Enrollment overlap visualization
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Drop-off trend charts
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Root cause distribution
                    </li>
                  </ul>
                </div>

                <div className="p-4 bg-white/10 backdrop-blur-sm rounded-lg">
                  <h4 className="font-bold mb-3 flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Detailed Report
                  </h4>
                  <ul className="space-y-2 text-sm text-white/90">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Comprehensive insights and findings
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Actionable recommendations
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Implementation roadmap
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Data Assets */}
          <Card className="border-l-4 border-green-600">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Database className="w-6 h-6 mr-3 text-green-600" />
                Available Data Assets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Based on our current WCD data infrastructure, we have the following assets available for analysis:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-green-50">
                  <CardContent className="pt-6">
                    <h5 className="font-semibold text-green-900 mb-2">Beneficiary Data</h5>
                    <p className="text-sm text-green-800">14,950 beneficiaries with Aadhaar verification (99.84%)</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="pt-6">
                    <h5 className="font-semibold text-green-900 mb-2">Enrollment Records</h5>
                    <p className="text-sm text-green-800">971,176 PURNA girls + 8,173 children monitored</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="pt-6">
                    <h5 className="font-semibold text-green-900 mb-2">Drop-off Tracking</h5>
                    <p className="text-sm text-green-800">6,864 drop-offs identified with 6,168 contactable</p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ObjectivePage;
